if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this._slideMap = new Map();
        this._thumbMap = new Map();
        this._indexList = [];
        this._currentIndex = 0;
        this._total = 0;
        this._counterCurrent = null;
        this._bound = false;
        this._onKeyDown = this._onKeyDown.bind(this);
        this._onBackdropClick = this._onBackdropClick.bind(this);
      }

      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();

        // Guard: only initialize once, even if the element is moved in the DOM
        if (this._bound) return;
        this._bound = true;

        this._cacheElements();
        this._bindEvents();
      }

      disconnectedCallback() {
        if (super.disconnectedCallback) super.disconnectedCallback();

        // Defer cleanup — ModalDialog moves us in DOM during connect,
        // which would otherwise wipe our listeners immediately.
        setTimeout(() => {
          if (!this.isConnected) {
            this._bound = false;
            document.removeEventListener('keydown', this._onKeyDown);
          }
        }, 0);
      }

      _cacheElements() {
        const slidesRaw = Array.from(this.querySelectorAll('.product-media-modal__slide'));
        const thumbsRaw = Array.from(this.querySelectorAll('.product-media-modal__thumbnail'));

        this._slideMap = new Map();
        this._thumbMap = new Map();

        slidesRaw.forEach((el) => {
          const idx = parseInt(el.getAttribute('data-media-index'), 10);
          if (!isNaN(idx)) this._slideMap.set(idx, el);
        });

        thumbsRaw.forEach((el) => {
          const idx = parseInt(el.getAttribute('data-media-index'), 10);
          if (!isNaN(idx)) this._thumbMap.set(idx, el);
        });

        this._indexList = Array.from(this._slideMap.keys()).sort((a, b) => a - b);

        this._total = this._indexList.length;
        this._counterCurrent = this.querySelector('[data-media-counter-current]');
      }

      _bindEvents() {
        this._thumbMap.forEach((thumb) => {
          thumb.addEventListener('click', (e) => {
            e.stopPropagation();
            this._activateByMediaId(thumb.getAttribute('data-media-id'));
          });
          thumb.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              thumb.click();
            }
          });
        });

        this.querySelectorAll('[data-media-nav]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (btn.getAttribute('data-media-nav') === 'prev') this.prev();
            else this.next();
          });
        });

        const closeBtn = this.querySelector('.product-media-modal__toggle');
        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.hide();
          });
        }

        const dialog = this.querySelector('.product-media-modal__dialog');
        if (dialog) {
          dialog.addEventListener('click', this._onBackdropClick);
        }

        document.addEventListener('keydown', this._onKeyDown);
      }

      _onBackdropClick(e) {
        if (e.target === this.querySelector('.product-media-modal__dialog')) {
          this.hide();
        }
      }

      _onKeyDown(e) {
        if (!this.hasAttribute('open') && !this.classList.contains('is-open')) return;

        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.prev();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.next();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          this.hide();
        }
      }

      show(opener) {
        super.show(opener);
        this._cacheElements();

        const openerMediaId = opener ? opener.getAttribute('data-media-id') : null;

        if (openerMediaId) {
          this._activateByMediaId(openerMediaId);
        } else {
          this._activateByIndex(0);
        }
      }

      hide() {
        super.hide();
      }

      _activateByMediaId(mediaId) {
        if (!mediaId) return;

        let position = -1;

        this._indexList.forEach((mediaIndex, pos) => {
          const slide = this._slideMap.get(mediaIndex);
          if (!slide) return;
          if (slide.getAttribute('data-media-id') === mediaId) {
            position = pos;
          }
        });

        if (position < 0) {
          const openerSuffix = String(mediaId).split('-').pop();
          this._indexList.forEach((mediaIndex, pos) => {
            const slide = this._slideMap.get(mediaIndex);
            if (!slide) return;
            const slideId = slide.getAttribute('data-media-id') || '';
            if (slideId === openerSuffix || slideId.endsWith('-' + openerSuffix)) {
              position = pos;
            }
          });
        }

        this._activateByIndex(position >= 0 ? position : 0);
      }

      _activateByIndex(position) {
        if (!this._total) return;

        if (position < 0) position = this._total - 1;
        if (position >= this._total) position = 0;

        this._currentIndex = position;

        const mediaIndex = this._indexList[position];

        this._slideMap.forEach((slide) => slide.classList.remove('is-active'));
        this._thumbMap.forEach((thumb) => thumb.classList.remove('is-active'));

        const activeSlide = this._slideMap.get(mediaIndex);
        const activeThumb = this._thumbMap.get(mediaIndex);

        if (activeSlide) activeSlide.classList.add('is-active');
        if (activeThumb) activeThumb.classList.add('is-active');

        if (this._counterCurrent) {
          this._counterCurrent.textContent = position + 1;
        }

        if (activeThumb) {
          activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }

        if (activeSlide) {
          const deferred = activeSlide.querySelector('deferred-media');
          if (deferred && typeof deferred.loadContent === 'function') {
            deferred.loadContent();
          }
        }
      }

      prev() {
        this._activateByIndex(this._currentIndex - 1);
      }

      next() {
        this._activateByIndex(this._currentIndex + 1);
      }
    }
  );
}